import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ColDef, ColDefField, GridOptions, SendToClipboardParams, ValueFormatterParams } from 'ag-grid-community';

import { compareStrings } from '@shure/shared/angular/ui/comparators/string';
import { NoRowsOverlayComponent, NoRowsOverlayParams } from '@shure/shared/angular/ui/components/ag-grid/overlays';
import { PLACEHOLDER_VALUE } from '@shure/shared/models';

import { MuteStatusCellData, MuteStatusRendererComponent } from '../cell-renderers';
import {
	DeviceStatusCellData,
	NetworkRendererComponent
} from '../cell-renderers/network-renderer/network-renderer.component';
import { ipAddressComparator } from '../comparators/ip-address-comparator';
import { macAddressComparator } from '../comparators/mac-address-comparator';
import { makeComparator } from '../comparators/make-comparator';
import { mapDeviceStatusToTranslocoKey } from '../mappers/device-status-mapper';
import { mapMuteStatusToTranslocoKey } from '../mappers/mute-status-mapper';

import { agGridLabelLookup } from './ag-grid-label-lookup';

export interface GetGridOptionsConfig<TRow> {
	/**
	 * Allow Excel & CSV export from the grid via AG Grid's built-in functionality.
	 * @default true
	 */
	allowExport?: boolean;

	/**
	 * Filter function to allow only certain columns to be copied to the clipboard.
	 * The column will only be copied if the filter returns true.
	 */
	clipboardColumnFilter?(colDef: ColDef<TRow>): boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEFAULT_GET_GRID_OPTIONS_CONFIG: GetGridOptionsConfig<any> = {
	allowExport: true
};

export type TextColumnOptions<TRow> = ColDef<TRow> & Required<Pick<ColDef<TRow>, 'field'>>;

export type CustomRendererColumnOptions<TRow> = ColDef<TRow> & Required<Pick<ColDef<TRow>, 'cellRenderer'>>;

/**
 * Generic factory class for creating common AG Grid options and column types. This class is used internally by the
 * `BaseTableComponent`, but may be used directly in place of the base table when composition is preferred over inheritance.
 */
@Injectable({ providedIn: 'root' })
export class AgGridFactory<TRow> {
	constructor(private readonly transloco: TranslocoService) {}

	public checkboxColumnOptions(): Pick<GridOptions<TRow>, 'selection' | 'selectionColumnDef'> {
		return {
			// Enable the checkbox column:
			// https://www.ag-grid.com/angular-data-grid/row-selection-multi-row/
			selection: {
				mode: 'multiRow',
				checkboxes: true, // this is Key for the column to be rendered
				headerCheckbox: true,
				selectAll: 'filtered',
				enableClickSelection: true,
				enableMultiSelectWithClick: false,
				copySelectedRows: true
			},

			// Column Defaults for the built-in select/checkbox column
			// https://www.ag-grid.com/angular-data-grid/row-selection-multi-row/#customising-the-checkbox-column
			//
			// If sorting is enabled, consider using the unSortIcon option:
			// https://www.ag-grid.com/angular-data-grid/column-properties/#reference-sort-unSortIcon
			selectionColumnDef: {
				sortable: false,
				resizable: false,
				minWidth: 42,
				maxWidth: 42,
				flex: 0,
				suppressHeaderContextMenu: true,
				pinned: 'left',
				lockPinned: true
			}
		};
	}

	/**
	 * Creates a set of default grid options to be used as a baseline for further table configuration
	 */
	public getDefaultGridOptions(
		config: GetGridOptionsConfig<TRow> = DEFAULT_GET_GRID_OPTIONS_CONFIG
	): GridOptions<TRow> {
		return {
			/**
			 * Column definition applied to columns in a grouping row
			 * https://ag-grid.com/angular-data-grid/grid-options/#reference-rowGrouping-autoGroupColumnDef
			 */
			autoGroupColumnDef: {
				minWidth: 200
			},
			/**
			 * Default strategy used for autosizing grid columns + some additional padding
			 * https://ag-grid.com/angular-data-grid/grid-options/#reference-columnSizing-autoSizeStrategy
			 */
			autoSizeStrategy: { type: 'fitGridWidth' },
			autoSizePadding: 24,
			/**
			 * Default column definition for every column in the grid
			 * https://ag-grid.com/angular-data-grid/grid-options/#reference-columns-defaultColDef
			 */
			defaultColDef: {
				minWidth: 150,
				wrapHeaderText: true,
				autoHeaderHeight: true,
				flex: 1, // by default, we want all columns to resize proportionally. This can be overridden.
				cellStyle: { display: 'block' } // ag-grid "feauture" that ensures ellipsis are used when column overflow
			},
			getContextMenuItems: config.allowExport
				? (): string[] => ['copy', 'copyWithHeaders', 'separator', 'export']
				: undefined,
			/**
			 * Get a translated grid string
			 * https://ag-grid.com/angular-data-grid/grid-options/#reference-localisation-getLocaleText
			 */
			getLocaleText: (params) => agGridLabelLookup(this.transloco, params.key),
			headerHeight: 36,
			noRowsOverlayComponent: NoRowsOverlayComponent,
			noRowsOverlayComponentParams: {
				title: this.transloco.translate('shAgGrid.ag-grid.noRowsToShow')
			} satisfies NoRowsOverlayParams,
			/**
			 * Setting popup parent to the body is necessary for column menu popups to extend outside of the table when
			 * the table is not tall enough to contain the popup.
			 * https://ag-grid.com/angular-data-grid/grid-options/#reference-accessories-popupParent
			 */
			popupParent: document.querySelector('body'),
			rowHeight: 36,
			selection: {
				mode: 'multiRow',
				selectAll: 'filtered',
				enableClickSelection: true,
				enableMultiSelectWithClick: false
			},
			sendToClipboard: config.clipboardColumnFilter
				? this.createClipboardHandler(config.clipboardColumnFilter)
				: undefined,
			suppressCellFocus: true,
			suppressContextMenu: !config.allowExport,
			suppressCsvExport: !config.allowExport,
			suppressExcelExport: !config.allowExport,
			suppressMenuHide: true
		};
	}

	/**
	 * Creates a hidden column that is used to sort the table in a consistent way by row ID
	 */
	public createDefaultSortIdColumn(field: ColDefField<TRow>): ColDef<TRow> {
		return { field, suppressColumnsToolPanel: true, hide: true, sortable: true, initialSort: 'asc' };
	}

	/**
	 * @deprecated Use new AG Grid selection options instead
	 */
	public createCheckboxColumn(options: Partial<ColDef<TRow>> = {}): ColDef<TRow> {
		return {
			headerCheckboxSelection: true,
			headerCheckboxSelectionFilteredOnly: true,
			checkboxSelection: true,
			suppressHeaderMenuButton: true,
			suppressColumnsToolPanel: true, // don't want this column showing up in menus
			suppressMovable: true,
			sortable: false,
			filter: false,
			resizable: false,
			minWidth: 42,
			maxWidth: 42,
			pinned: 'left',
			lockPinned: true,
			lockPosition: true,
			flex: 0,
			...options
		};
	}

	/**
	 * Creates a column definition for a basic text column with sorting and filtering enabled by default
	 */
	public createTextColumn(options: TextColumnOptions<TRow>): ColDef<TRow> {
		return {
			sortable: true,
			filter: true,
			...options
		};
	}

	public createNetworkStatusColumn(options: Partial<ColDef<TRow>> = {}): ColDef<TRow> {
		return {
			cellDataType: false,
			cellRenderer: NetworkRendererComponent,
			enableRowGroup: true,
			filter: true,
			minWidth: 150,
			headerName: this.transloco.translate('shAgGrid.common.labels.network'),
			comparator: makeComparator<DeviceStatusCellData>((a, b) => {
				const labelKeyA = mapDeviceStatusToTranslocoKey(a?.status);
				const labelKeyB = mapDeviceStatusToTranslocoKey(b?.status);
				return compareStrings(this.transloco.translate(labelKeyA), this.transloco.translate(labelKeyB));
			}),
			filterValueGetter: (params): string => {
				// The CellRenderer allows the field value to be null and undefined,
				// UX wants to display '--' not blank.
				const fieldValue = params.getValue(<string>options.field);
				if (!fieldValue) {
					return '--';
				}
				const labelKey = mapDeviceStatusToTranslocoKey(fieldValue.status);
				return this.transloco.translate(labelKey);
			},
			filterParams: {
				valueFormatter: (params: ValueFormatterParams): string => params.value
			},
			getQuickFilterText: (params): string => params.value,
			keyCreator: (params): string => {
				let retVal = '';
				if (typeof params.value === 'object') {
					const labelKey = mapDeviceStatusToTranslocoKey(params.value.status);
					retVal = this.transloco.translate(labelKey);
				} else {
					retVal = params.value;
				}
				return retVal;
			},
			valueFormatter: (params): string => {
				const status = params.value.status;
				if (!status) return '';
				return this.transloco.translate(mapDeviceStatusToTranslocoKey(status));
			},
			...options
		};
	}

	public createMuteStatusColumn(options: Partial<ColDef<TRow>> = {}): ColDef<TRow> {
		return {
			cellDataType: false,
			cellRenderer: MuteStatusRendererComponent,
			enableRowGroup: true,
			filter: true,
			minWidth: 150,
			headerName: this.transloco.translate('shAgGrid.common.labels.mute-status'),
			comparator: makeComparator<MuteStatusCellData>((a, b) => {
				const aString =
					a?.isMuted === undefined
						? PLACEHOLDER_VALUE
						: this.transloco.translate(mapMuteStatusToTranslocoKey(a.isMuted));
				const bString =
					b?.isMuted === undefined
						? PLACEHOLDER_VALUE
						: this.transloco.translate(mapMuteStatusToTranslocoKey(b.isMuted));
				return compareStrings(aString, bString);
			}),
			filterValueGetter: (params): string => {
				const fieldValue = params.getValue('muteStatusCellData');
				return fieldValue.isMuted === undefined
					? PLACEHOLDER_VALUE
					: this.transloco.translate(mapMuteStatusToTranslocoKey(fieldValue.isMuted));
			},
			filterParams: {
				valueFormatter: (params: ValueFormatterParams): string => params.value
			},
			getQuickFilterText: (params): string => params.value,
			keyCreator: (params): string => {
				let retVal = '';
				if (typeof params.value === 'object') {
					retVal =
						params.value.isMuted === undefined
							? PLACEHOLDER_VALUE
							: this.transloco.translate(mapMuteStatusToTranslocoKey(params.value.isMuted));
				} else {
					retVal = params.value;
				}
				return retVal;
			},
			...options
		};
	}

	/**
	 * Creates a text column for an IP address field
	 */
	public createIpAddressColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return {
			headerName: this.transloco.translate('shAgGrid.common.network.ip-address'),
			comparator: (a?: string, b?: string) => ipAddressComparator(a, b),
			minWidth: 130,
			...options
		};
	}

	/**
	 * Creates a text column for a serial number field
	 */
	public createSerialNumberColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return {
			headerName: this.transloco.translate('shAgGrid.common.labels.serial-number'),
			enableRowGroup: false,
			getQuickFilterText: (params) => params.value?.toLowerCase() ?? '',
			...options
		};
	}

	/**
	 * Creates a text column for a MAC address field
	 */
	public createMacAddressColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return {
			headerName: this.transloco.translate('shAgGrid.common.network.mac-address'),
			enableRowGroup: false,
			comparator: (a?: string, b?: string) => macAddressComparator(a, b),
			getQuickFilterText: (params) => params.value?.toLowerCase() ?? '',
			minWidth: 130,
			...options
		};
	}

	/**
	 * Creates a column definition for a column with a custom cell renderer
	 */
	public createCustomRendererColumn(options: CustomRendererColumnOptions<TRow>): ColDef<TRow> {
		return {
			cellDataType: false,
			...options
		};
	}

	/**
	 * Custom copy handler to allow filtering of columns that get copied
	 */
	public createClipboardHandler(
		columnFilter: (colDef: ColDef<TRow>) => boolean
	): (params: SendToClipboardParams) => void {
		return (params) => {
			// Get column definitions to determine indices to include
			const colDefs = params.api.getAllDisplayedColumns().map((col) => col.getColDef());
			const includedColIndices = colDefs
				.map((colDef, index) => (columnFilter(colDef) ? index : -1))
				.filter((index) => index > -1);

			// Split data into rows and columns
			const rows = params.data.split('\n').map((row) => row.split('\t'));

			// If row contains single column, write it
			if (rows.every((row) => row.length === 1)) {
				navigator.clipboard.writeText(params.data);
				return;
			}

			// Otherwise, only include columns that passed the filter
			const filteredRows = rows.map((row) => row.filter((_cell, index) => includedColIndices.includes(index)));

			// Reassemble filtered data into a format suitable for the clipboard
			const filteredData = filteredRows.map((row) => row.join('\t')).join('\n');
			navigator.clipboard.writeText(filteredData);
		};
	}
}
